import LightningIcon from '../../../components/LightningIcon';
import FastForwardIcon from '../../../components/FastForwardIcon';
import TrophyIcon from '../../../components/TrophyIcon';
import { listify } from '@siteless/util';
import events from './events';
import { ObjectiveStateResourceType } from '../../ObjectiveState';
import { SingleLanguageCopyType } from '../../../assets/copy';
import { tmdbConfig, oscarDatabase } from '../../../config/constants';

export let optionTypes = {
  winner: {
    key: 'winner',
    Icon: TrophyIcon,
    getOptionComponentProps: ({
      data: { points, categoryKey, nomineeId },
      objectiveState,
      copy,
    }: {
      data: {
        points: number;
        categoryKey: keyof typeof oscarDatabase.nomineeCategories;
        nomineeId: string;
      };
      objectiveState: ObjectiveStateResourceType;
      copy: SingleLanguageCopyType;
    }) => {
      let { type, nominees } = objectiveState.nomineeCategories[categoryKey];
      let { filmId, peopleIds, detail } = nominees[
        nomineeId as keyof typeof nominees
      ] as {
        filmId: keyof typeof objectiveState.films;
        peopleIds: (keyof typeof objectiveState.people)[];
        detail?: string;
      };
      let film = objectiveState.films[filmId];
      let people = peopleIds.map(id => objectiveState.people[id].name);

      let displayPoints = `+ ${points} ${copy.pointsIfThisNomineeWins}.`;

      let filmPosterUrl: string | undefined;
      if ('tmdb_data' in film && film.tmdb_data.tmdb_poster_path)
        filmPosterUrl = [
          tmdbConfig.base_url,
          tmdbConfig.poster_sizes.w185,
          film.tmdb_data.tmdb_poster_path,
        ].join('');

      if (type === 'performance') {
        let performer = objectiveState.people[peopleIds[0]];
        let performerPosterUrl: string | undefined;
        if ('tmdb_data' in performer && performer.tmdb_data.tmdb_profile_path)
          performerPosterUrl = [
            tmdbConfig.base_url,
            tmdbConfig.poster_sizes.w185,
            performer.tmdb_data.tmdb_profile_path,
          ].join('');

        return {
          favoriteConfig: {
            type: 'film',
            key: film.id,
          },
          suportFavoriteConfig: {
            type: 'person',
            key: performer.id,
          },
          posterImageUrl: filmPosterUrl,
          supportImageUrl: performerPosterUrl,
          title: people[0],
          details: `${copy.In} "${film.title}"`,
          displayPoints,
          filmIsNominee: false,
        } as const;
      }

      if (type === 'song')
        return {
          favoriteConfig: {
            type: 'film',
            key: film.id,
          },
          supportImageUrl: undefined,
          posterImageUrl: filmPosterUrl,
          title: detail as string,
          details: `${copy.From} ${film.title}; ${listify(people, copy.and)}`,
          displayPoints,
          filmIsNominee: false,
        } as const;

      if (type === 'international_film')
        return {
          favoriteConfig: {
            type: 'film',
            key: film.id,
          },
          supportImageUrl: undefined,
          posterImageUrl: filmPosterUrl,
          title: film.title,
          displayPoints,
          details: detail,
          filmIsNominee: true,
        } as const;

      return {
        favoriteConfig: {
          type: 'film',
          key: film.id,
        },
        supportImageUrl: undefined,
        posterImageUrl: filmPosterUrl,
        title: film.title,
        displayPoints,
        details: listify(people, copy.and),
        filmIsNominee: true,
      } as const;
    },
  },
  eventBoolean: {
    key: 'eventBoolean',
    Icon: LightningIcon,
    getOptionComponentProps: ({
      data: { points, eventName },
      copy,
    }: {
      data: { points: number; eventName: keyof typeof events };
      copy: SingleLanguageCopyType;
    }) => ({
      title: events[eventName]?.getTitle(copy),
      displayPoints: `+ ${points} ${copy.pointsIf}...`,
      details: events[eventName]?.getDescription(copy),
      supportImageUrl: undefined,
      posterImageUrl: undefined,
      favoriteConfig: undefined,
    }),
  },
  eventCount: {
    key: 'eventCount',
    Icon: LightningIcon,
    getOptionComponentProps: ({
      data: { points, eventName },
      copy,
    }: {
      data: { points: number; eventName: keyof typeof events };
      copy: SingleLanguageCopyType;
    }) => ({
      title: events[eventName]?.getTitle(copy),
      displayPoints: `+ ${points} ${copy.pointsEveryTime}...`,
      details: events[eventName]?.getDescription(copy),
      supportImageUrl: undefined,
      posterImageUrl: undefined,
      favoriteConfig: undefined,
    }),
  },
  pass: {
    key: 'pass',
    Icon: FastForwardIcon,
    getOptionComponentProps: ({
      data: { points },
      copy,
    }: {
      data: { points: number };
      copy: SingleLanguageCopyType;
    }) => ({
      title: copy._Pass,
      displayPoints: `+ ${points} ${copy.pointsNoMatterWhat}.`,
      details: undefined,
      supportImageUrl: undefined,
      posterImageUrl: undefined,
      favoriteConfig: undefined,
    }),
  },
} as const;

export default optionTypes;
